import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";
import { Users, Search, Phone, Calendar, AlertCircle } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

type Emp = {
  id: string;
  name: string;
  email: string;
  department: string | null;
  role: string;
  jobTitle: string | null;
  hiringDate: string | null;
  personalPhone: string | null;
  iqamaExpiry: string | null;
  passportExpiry: string | null;
  employmentStatus: string | null;
};

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString();
}

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (isNaN(target)) return null;
  const now = Date.now();
  return Math.ceil((target - now) / (1000 * 60 * 60 * 24));
}

function ExpiryBadge({ iso, label }: { iso: string | null; label: string }) {
  if (!iso) return <span className="text-gray-300">—</span>;
  const days = daysUntil(iso);
  if (days === null) return <span className="text-gray-300">—</span>;
  const color =
    days < 0 ? "text-red-700 bg-red-50" :
    days <= 30 ? "text-orange-700 bg-orange-50" :
    days <= 90 ? "text-amber-700 bg-amber-50" :
    "text-green-700 bg-green-50";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${color}`} title={`${label}: ${fmtDate(iso)}`}>
      {days < 0 ? <><AlertCircle size={11}/> Expired</> : <>{days}d</>}
    </span>
  );
}

const STATUS_COLOR: Record<string, string> = {
  ACTIVE:      "bg-green-50 text-green-700",
  PROBATION:   "bg-blue-50 text-blue-700",
  SUSPENDED:   "bg-amber-50 text-amber-700",
  TERMINATED:  "bg-red-50 text-red-700",
};

export default function HR() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<Emp[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("");
  const [status, setStatus] = useState("");

  const canManage = user?.role === "ADMIN" || user?.role === "HR";

  useEffect(() => {
    setLoading(true);
    api.get<Emp[]>("/hr/employees")
      .then(r => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  const departments = useMemo(() => {
    const s = new Set<string>();
    rows.forEach(r => r.department && s.add(r.department));
    return Array.from(s).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return rows.filter(r => {
      if (dept && r.department !== dept) return false;
      if (status && r.employmentStatus !== status) return false;
      if (!t) return true;
      return (
        r.name.toLowerCase().includes(t) ||
        (r.email || "").toLowerCase().includes(t) ||
        (r.jobTitle || "").toLowerCase().includes(t) ||
        (r.personalPhone || "").includes(t)
      );
    });
  }, [rows, q, dept, status]);

  return (
    <div className="max-w-7xl mx-auto p-3 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <Users className="text-indigo-600" size={26}/>
          <div>
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900">HR — Employees</h1>
            <p className="text-xs sm:text-sm text-gray-500">
              {canManage ? `${filtered.length} employees` : "Your profile"}
            </p>
          </div>
        </div>
      </div>

      {canManage && (
        <div className="bg-white border border-gray-200 rounded-xl p-3 mb-4 grid grid-cols-1 sm:grid-cols-4 gap-2">
          <div className="relative sm:col-span-2">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Name / email / title / phone"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm"/>
          </div>
          <select value={dept} onChange={e => setDept(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm">
            <option value="">All departments</option>
            {departments.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={status} onChange={e => setStatus(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm">
            <option value="">All status</option>
            <option value="ACTIVE">Active</option>
            <option value="PROBATION">Probation</option>
            <option value="SUSPENDED">Suspended</option>
            <option value="TERMINATED">Terminated</option>
          </select>
        </div>
      )}

      {loading ? (
        <div className="p-6 text-gray-500">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-400">
          No employees found.
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-3 py-3 font-medium text-gray-600">Employee</th>
                <th className="text-left px-3 py-3 font-medium text-gray-600">Title</th>
                <th className="text-left px-3 py-3 font-medium text-gray-600">Department</th>
                <th className="text-left px-3 py-3 font-medium text-gray-600">Hired</th>
                <th className="text-left px-3 py-3 font-medium text-gray-600">Phone</th>
                <th className="text-left px-3 py-3 font-medium text-gray-600">Iqama</th>
                <th className="text-left px-3 py-3 font-medium text-gray-600">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(r => (
                <tr key={r.id} onClick={() => navigate(`/hr/${r.id}`)}
                    className="hover:bg-indigo-50 cursor-pointer">
                  <td className="px-3 py-3">
                    <div className="font-medium text-gray-900">{r.name}</div>
                    <div className="text-xs text-gray-500">{r.email}</div>
                  </td>
                  <td className="px-3 py-3 text-gray-700 text-sm">{r.jobTitle || <span className="text-gray-300">—</span>}</td>
                  <td className="px-3 py-3 text-gray-600 text-sm">{r.department || "—"}</td>
                  <td className="px-3 py-3 text-gray-600 text-sm whitespace-nowrap">
                    <Calendar size={12} className="inline text-gray-400 mr-1"/>
                    {fmtDate(r.hiringDate)}
                  </td>
                  <td className="px-3 py-3 text-gray-600 text-sm whitespace-nowrap">
                    {r.personalPhone ? (
                      <a href={`tel:${r.personalPhone}`} onClick={e => e.stopPropagation()} className="hover:text-indigo-600 flex items-center gap-1">
                        <Phone size={11}/>{r.personalPhone}
                      </a>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-3">
                    <ExpiryBadge iso={r.iqamaExpiry} label="Iqama expiry"/>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-medium ${STATUS_COLOR[r.employmentStatus || "ACTIVE"] || "bg-gray-50 text-gray-600"}`}>
                      {r.employmentStatus || "ACTIVE"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
